import { useCallback, useEffect, useRef, useState } from 'react';

import { ClaimAbortReason, ClaimIntent, resolveClaim, seatStateFor } from './seatClaim';

/** Re-evaluation cadence, matching useWaitlistCountdown's ticker. */
const TICK_MS = 250;

export type SeatClaimPhase = 'idle' | 'holding' | 'sent';

type UseSeatClaimInput = {
  /** The offered table's broadcast hand phase. */
  hand: unknown;
  /** The offered table's broadcast players array (index === engine seat id). */
  players: unknown;
  /** Am I already seated at the offered table? */
  isSeated: boolean;
  /** My current offer-window signature for that table, or null if I hold none. */
  currentOfferSig: string | null;
  /** Fire the actual seat request. Called at most once per hold. */
  send: (intent: ClaimIntent) => void;
  /**
   * Terminal outcome for a held claim. Not called when the caller clears it.
   *
   * `alreadySent` says whether the request had left the client before this
   * outcome. It matters for messaging: once the request is out, the ENGINE
   * answers it (a gotJoinGame rejection renders through ErrorMsgModal), so a
   * caller that also shows its own copy would stack two modals describing the
   * same rejection. Callers should stay silent when `alreadySent` is true.
   */
  onResolved: (reason: ClaimAbortReason, intent: ClaimIntent, alreadySent: boolean) => void;
  now?: () => number;
  tickMs?: number;
};

/**
 * Holds a seat-taking intent across the dealer/SB/BB assignment window and fires
 * it the moment the table is joinable again, instead of dropping it with a
 * terminal "Unable to take seat" dialog (see seatClaim.ts for the why).
 *
 * Callers keep their existing synchronous fast path for a joinable table and
 * only hold() when the table is mid-setup, so the common path is untouched — all
 * of this machinery is confined to the branch that was previously a dead end.
 *
 * Safety properties:
 *   • single-flight — at most one send per held intent, so no duplicate claims;
 *   • it can only ever stop retrying, never assert a seating the engine did not
 *     grant (the atomic seat UPDATE is the sole authority);
 *   • a revoked/bumped offer or an elapsed deadline aborts, so a claim can never
 *     seat a player after their offer window is gone.
 */
export const useSeatClaim = ({
  hand,
  players,
  isSeated,
  currentOfferSig,
  send,
  onResolved,
  now = Date.now,
  tickMs = TICK_MS
}: UseSeatClaimInput) => {
  const [phase, setPhase] = useState<SeatClaimPhase>('idle');
  const [tick, setTick] = useState(0);
  const intentRef = useRef<ClaimIntent | null>(null);
  const sentRef = useRef(false);

  // Latest callbacks in refs so a caller passing fresh closures each render
  // cannot restart the ticker or re-run the decision effect.
  const sendRef = useRef(send);
  sendRef.current = send;
  const resolvedRef = useRef(onResolved);
  resolvedRef.current = onResolved;
  const nowRef = useRef(now);
  nowRef.current = now;

  /** Capture the player's intent and hold it until the table can seat them. */
  const hold = useCallback((intent: ClaimIntent) => {
    intentRef.current = intent;
    sentRef.current = false;
    setPhase('holding');
  }, []);

  /** Drop a held claim without reporting an outcome (player cancelled). */
  const clear = useCallback(() => {
    intentRef.current = null;
    sentRef.current = false;
    setPhase('idle');
  }, []);

  // Deadline ticker — only runs while a claim is live, so an idle table costs
  // nothing.
  useEffect(() => {
    if (phase === 'idle') return;
    const id = setInterval(() => setTick((t) => t + 1), tickMs);
    return () => clearInterval(id);
  }, [phase, tickMs]);

  // Re-evaluate on every table-state change and on every tick.
  useEffect(() => {
    const intent = intentRef.current;
    if (!intent || phase === 'idle') return;

    const decision = resolveClaim({
      intent,
      hand,
      seatState: seatStateFor(players, intent.seatId),
      isSeated,
      currentOfferSig,
      nowMs: nowRef.current()
    });

    if (decision.action === 'wait') return;

    if (decision.action === 'send') {
      if (sentRef.current) return; // single-flight
      sentRef.current = true;
      setPhase('sent');
      sendRef.current(intent);
      return;
    }

    // Terminal: clear before notifying so a re-render triggered by the callback
    // cannot re-enter this branch for the same intent.
    const alreadySent = sentRef.current;
    intentRef.current = null;
    sentRef.current = false;
    setPhase('idle');
    resolvedRef.current(decision.reason, intent, alreadySent);
  }, [hand, players, isSeated, currentOfferSig, phase, tick]);

  return {
    phase,
    /** True while an intent is held or in flight — drives the pending UI. */
    isClaiming: phase !== 'idle',
    hold,
    clear
  };
};

export default useSeatClaim;
